<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'settings',
    path: '/settings',
    component: 'Settings',
    icon: 'mdi:cog',
    title: () => gettext('Settings'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Settings',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const sidebarItems = ref([
    {
      id: 'general',
      name: $fn.value.gettext('General'),
      to: {
        name: 'app.settings.general',
      },
    },
    {
      id: 'mailAccounts',
      name: $fn.value.gettext('Mail accounts'),
      to: {
        name: 'app.settings.mailAccounts',
      },
    },
    {
      id: 'printers',
      name: $fn.value.pgettext('Settings', 'Printers'),
      to: {
        name: 'app.settings.printers',
      },
    },
    {
      id: 'groups',
      name: $fn.value.gettext('Groups'),
      to: {
        name: 'app.settings.groups',
      },
    },
    {
      id: 'tags',
      name: $fn.value.gettext('Tags'),
      to: {
        name: 'app.settings.tagsEditor',
      },
    },
    {
      id: 'numberings',
      name: $fn.value.gettext('Numberings'),
      to: {
        name: 'app.settings.numberings',
      },
    },
    {
      id: 'statData',
      name: $fn.value.gettext('External stat data'),
      to: {
        name: 'app.settings.statData',
      },
    },
    {
      id: 'tasks',
      name: $fn.value.gettext('Tasks'),
      to: {
        name: 'app.settings.tasks',
      },
    },
    {
      id: 'jobSchedulerConfigurations',
      name: $fn.value.pgettext('Settings', 'Job scheduler'),
      to: {
        name: 'app.settings.jobScheduler',
      },
    },
    {
      id: 'expenseTypes',
      name: $fn.value.gettext('Expense types'),
      to: {
        name: 'app.settings.expenseTypes',
      },
    },
    {
      id: 'invocing',
      name: $fn.value.pgettext('Settings', 'Invoicing'),
      to: {
        name: 'app.settings.invoicing',
      },
    },
    {
      id: 'vatPeriods',
      name: $fn.value.pgettext('Settings', 'VAT periods'),
      to: {
        name: 'app.settings.vatPeriods',
      },
    },
    {
      id: 'units',
      name: $fn.value.pgettext('Settings', 'Units'),
      to: {
        name: 'app.settings.units',
      },
    },
  ]);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));

  const $data = computed(() => ({
    sidebarItems: sidebarItems.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'sidebarItems' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      sidebarItems,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Settings') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_7s37plnvm_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_7s37plnvm =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_7s37plnvm_getActionArgs,
    });
  const inst_evh_7315092382398562_close_1wlatqxql =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_7s37plnvm,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_1wlatqxql],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_mwv0sm7yz =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_t2fqlivwk =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_mwv0sm7yz,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_t2fqlivwk],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_1wlatqxql,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_t2fqlivwk,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_close_1wlatqxql = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_1wlatqxql.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_t2fqlivwk = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_t2fqlivwk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Settings"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-hidden h-100 d-flex">
      <ItpSidebar :default-width="400" class="me-3">
        <BListGroup>
          <router-link
            :key="item.id"
            :to="item.to"
            custom
            v-slot="{ href, navigate, isActive }"
            v-for="item in sidebarItems"
          >
            <BListGroupItem
              :class="{ 'fw-bold': isActive }"
              class="d-flex justify-content-between align-items-center py-2"
            >
              <BLink
                :href="href"
                @click="navigate"
                class="text-truncate text-decoration-none itp-sidebar-link"
              >
                {{ item.name }}
              </BLink>
            </BListGroupItem>
          </router-link>
        </BListGroup>
      </ItpSidebar>
      <router-view class="h-100 w-100 overflow-hidden"> </router-view>
    </div>
  </ItpPage>
</template>
